import React, { useEffect, useCallback, useRef } from 'react';
import { copy, Language } from '../content/i18n';

interface VideoModalProps {
  url: string;
  isOpen: boolean;
  onClose: () => void;
  language: Language;
}

const VideoModal: React.FC<VideoModalProps> = ({ url, isOpen, onClose, language }) => {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const t = copy[language].videoModal;

  const playerUrl = (() => {
    try {
      const parsedUrl = new URL(url);

      if (parsedUrl.hostname.includes('vimeo.com')) {
        parsedUrl.searchParams.set('quality', '1080p');
      }

      return parsedUrl.toString();
    } catch {
      return url;
    }
  })();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      // Focus close button for accessibility
      setTimeout(() => closeBtnRef.current?.focus(), 50);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t.dialogLabel}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.92)',
        backdropFilter: 'blur(6px)',
      }}
    >
      {/* Close button */}
      <button
        ref={closeBtnRef}
        onClick={onClose}
        aria-label={t.closeAria}
        style={{
          position: 'absolute',
          top: 16,
          right: 24,
          zIndex: 51,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'rgba(255,255,255,0.7)',
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '0.75rem',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          transition: 'color 0.2s ease',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#c49428'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.7)'; }}
      >
        <span style={{ fontSize: '1.2rem' }}>✕</span> {t.closeText}
      </button>

      {/* Player container */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 1100,
          margin: '0 16px',
          aspectRatio: '16/9',
          boxShadow: '0 0 60px rgba(196,148,40,0.25), 0 0 120px rgba(196,148,40,0.1)',
        }}
      >
        <iframe
          src={playerUrl}
          title="vimeo-player"
          width="100%"
          height="100%"
          frameBorder="0"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          allowFullScreen
          style={{ display: 'block', border: 'none' }}
        />
      </div>
    </div>
  );
};

export default VideoModal;
